import React, { PropsWithChildren } from 'react';
import styled, { css } from 'styled-components';
import { Icon } from '../icon';
import { Loader } from '../loader';
import { PropsWithClassName } from '../tabselector/PropsWithClassName';
import { DropdownSize, DropdownSizes } from './DropdownSize';
import { transition } from '../themes/mainTheme';

// Stroke thickens on press, and on an open box at rest, so the box reads as
// engaged even where the border colour does not change. The extra 0.5px is an
// inset shadow, not a wider border — a wider border would grow the box by 1px
// and shift the layout.
// eslint-disable-next-line no-magic-numbers
const pressedInset = 0.5;

const StyledChevron = styled(Icon)`
	flex: 0 1 auto;
`;

export const StyledDropdownBox = styled.div<{
	$open: boolean;
	$restricted?: boolean;
	$disabled?: boolean;
	$hasError?: boolean;
	$size: DropdownSize;
}>`
	font-family: ${props => props.theme.font};
	font-weight: 510;
	flex: 1 0 auto;
	display: flex;
	align-items: center;
	box-sizing: border-box;
	overflow: hidden;
	cursor: pointer;
	border-radius: 4px;
	border: 1px solid ${props => props.theme.form.dropdown.box.border};
	background: ${props => props.theme.form.dropdown.box.background};
	color: ${props => props.theme.form.dropdown.box.color};
	transition: ${transition('background', 'border-color', 'color')};

	${({ $size }) => {
		const size = DropdownSizes[$size];
		return css`
			height: ${size.height}px;
			gap: ${size.gap}px;
			padding: 0 ${size.paddingRight}px 0 ${size.paddingLeft}px;
			font-size: ${size.fontSize}px;
			letter-spacing: ${size.letterSpacing}px;

			& ${Icon.cssSelector} {
				font-size: ${size.iconSize}px;
			}
		`;
	}}

	// Icons take the box's colour rather than carrying their own set of states.
	// The chevron is the exception below — it leads the box.
	${Icon.cssSelector} {
		color: inherit;
	}

	${StyledChevron} {
		color: ${props => props.theme.form.dropdown.box.chevron};
		transition: ${transition('color', 'transform')};
	}

	// Mouse-down fires :focus but not :focus-visible — without this the browser
	// paints its own focus ring on press.
	&:focus {
		outline: none;
	}

	// Ring is drawn with box-shadow, so keep a transparent outline for Windows
	// High Contrast Mode, where box-shadow is not rendered.
	&:focus-visible {
		outline: 2px solid transparent;
		outline-offset: 2px;
		border-color: ${props => props.theme.form.dropdown.box.hover.border};
		box-shadow: ${props => props.theme.shadow.focus};
	}

	&:hover {
		border-color: ${props => props.theme.form.dropdown.box.hover.border};
		color: ${props => props.theme.form.dropdown.box.hover.color};

		${StyledChevron} {
			color: ${props => props.theme.form.dropdown.box.hover.chevron};
		}
	}

	&:active {
		border-color: ${props => props.theme.form.dropdown.box.pressed.border};
		box-shadow: inset 0 0 0 ${pressedInset}px ${props => props.theme.form.dropdown.box.pressed.border};

		${StyledChevron} {
			color: ${props => props.theme.form.dropdown.box.pressed.chevron};
		}
	}

	// Open is a resting state of its own, so it restates hover and press rather
	// than layering on top of the closed ones.
	${({ $open, theme }) => $open && css`
		background: ${theme.form.dropdown.box.open.background};
		border-color: ${theme.form.dropdown.box.open.border};
		color: ${theme.form.dropdown.box.open.color};
		box-shadow: inset 0 0 0 ${pressedInset}px ${theme.form.dropdown.box.open.border};

		${StyledChevron} {
			color: ${theme.form.dropdown.box.open.chevron};
			transform: rotate(180deg);
		}

		&:hover,
		&:active {
			border-color: ${theme.form.dropdown.box.open.engagedBorder};
			color: ${theme.form.dropdown.box.open.engagedColor};
			box-shadow: none;

			${StyledChevron} {
				color: ${theme.form.dropdown.box.open.engagedChevron};
			}
		}
	`}

	${({ $hasError, theme }) => $hasError && css`
		border-color: ${theme.form.input.error.border};
		box-shadow: ${theme.form.input.error.shadow};
	`}

	${({ $restricted, theme }) => $restricted && css`
		border-color: ${theme.form.dropdown.restricted.border};
	`}

	${({ $disabled, theme }) => $disabled && css`
		&, &:hover, &:active {
			background: ${theme.form.dropdown.box.disabled.background};
			color: ${theme.form.dropdown.box.disabled.color};
			border-color: ${theme.form.dropdown.box.border};
			box-shadow: none;
			opacity: ${theme.form.disabled.opacity};
			cursor: auto;

			${StyledChevron} {
				color: ${theme.form.dropdown.box.disabled.color};
			}
		}
	`}
`;

const StyledLoader = styled(Loader)`
	flex: 0 1 auto;
	margin: 2px;
`;

type BoxProps = {
	disabled?: boolean,
	hasError?: boolean,
	isOpen: boolean,
	loading?: boolean,
	onClose: () => void,
	onOpen: () => void,
	onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void,
	restricted?: boolean,
	/** The box is a text field while searching, so a click belongs to the caret. */
	searchMode?: boolean,
	size: DropdownSize,
	tabbable?: boolean,
};

export const Box = React.forwardRef<HTMLDivElement, PropsWithClassName<PropsWithChildren<BoxProps>>>(({
	className,
	disabled,
	hasError,
	isOpen,
	loading,
	onClose,
	onOpen,
	onKeyDown,
	restricted,
	searchMode,
	size,
	tabbable,
	children
}, buttonRef) => {

	// Closing should cost the same click as opening, so the whole box toggles —
	// hunting for the chevron to close what any pixel opened is the kind of thing
	// only the person who built it knows. Search mode is the exception: there the
	// box is a text field, and the chevron keeps the job.
	const onBoxClick = loading || disabled
		? undefined
		: (isOpen ? (searchMode ? undefined : onClose) : onOpen);

	return (
		<StyledDropdownBox
			ref={buttonRef}
			tabIndex={tabbable ? (disabled ? -1 : 0) : undefined}
			onClick={onBoxClick}
			onKeyDown={tabbable ? onKeyDown : undefined}
			className={className}
			$size={size}
			$open={isOpen}
			$disabled={disabled}
			$hasError={hasError}
			$restricted={restricted}>
			{children}
			{loading
				? <StyledLoader />
				: <StyledChevron
					icon={'dropdown-chevron'}
					onClick={isOpen && searchMode ? onClose : undefined} />}
		</StyledDropdownBox>
	);
});

Box.displayName = 'DropdownBox';
